import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { constants } from 'config/constants';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteDeviceGroupsModal = ({ children, groupIDs = [], visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();
	const canDeleteGroup = useManagePermittedRoles('deleteGroup');

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	// ✅ Get all asset groups from store
	const assetGroups = useSelector(selectAssetGroups);

	// ✅ Get loading and success states
	const { loadingDeleteGroup, completeDeleteGroup } = useSelector((state) => state.AssetGroups);

	// ✅ Filter selected groups
	const filteredGroups = useMemo(
		() => assetGroups.filter((group) => groupIDs.includes(group.groupID)),
		[assetGroups, groupIDs]
	);

	// ✅ Generate selected group names
	const selectedNames = useMemo(() => filteredGroups.map((group) => group.name || '-'), [filteredGroups]);
	const hasInnerRangeGroup = useMemo(
		() => filteredGroups.some((group) => group?.typeID === constants.INNER_RANGE),
		[filteredGroups]
	);

	// ✅ Dynamic text
	const generateDeleteTitle = () =>
		groupIDs.length > 1
			? `Are you sure you want to delete these ${groupIDs.length} device groups?`
			: 'Are you sure you want to delete this device group?';

	const generateDeleteButtonText = () => (groupIDs.length > 1 ? 'Delete Groups' : 'Delete');
	const isTriggerDisabled = loadingDeleteGroup || !canDeleteGroup || hasInnerRangeGroup;
	const triggerTitle = !canDeleteGroup
		? permissionDeniedMessage
		: hasInnerRangeGroup
			? 'Deletion disabled. Managed by Inner Range hardware to protect synchronization integrity.'
			: 'Delete this Device Group';

	// ✅ Close modal
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle deletion
	const handleDelete = () => dispatch(assetGroupActions.deleteGroup({ groupIDs }));

	// ✅ Close on success
	useEffect(() => {
		if (completeDeleteGroup) handleCancel();
	}, [completeDeleteGroup, handleCancel]);

	return (
		<>
			<Tooltip title={triggerTitle} placement='top'>
				<span onClick={(e) => !isTriggerDisabled && canDeleteGroup && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							disabled={isTriggerDisabled}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingDeleteGroup} block>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							loading={loadingDeleteGroup}
							disabled={loadingDeleteGroup}
							type='primary'
							danger
							block
						>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Device Groups</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteDeviceGroupsModal;
